import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  CLUB_FILTERS,
  MAX_PAGE,
  RESERVATION_SORTS,
  RESERVATION_TYPES,
  ReservationStatus,
  SITE_FILTER_LABEL,
  SITE_FILTERS,
  SOURCE_FILTERS,
  type StatsBucket,
  type StatsDimension,
  type StatsGroup,
} from '@smile/shared';
import { api, ApiError } from '../api/client';
import { GroupReport, SoldReport, AdsReport, DemandReportView, HoursReportView, UsageReportView, AutoTargetReportView } from '../components/Sestavy';
import { can } from '../lib/perms';
import {
  CZK,
  LOCATION_LABEL,
  PCT,
  SOURCE_LABEL,
  STATUS_LABEL,
  TIER_LABEL,
  TYPE_LABEL, TYPE_SHORT,
  rezWord,
} from '../lib/format';
import {
  DATEFIELD_LABEL,
  type DateField,
  type Filters,
  isSearching,
  makeDefaults,
  oneOf,
  pageParam,
  parseFilters,
  PAST_QUICK_RANGES,
  quickRangeKey,
  SEARCH_DEBOUNCE_MS,
  STATS_LOOKBACK_DAYS,
  statsDefaults,
  statsUnit,
  toQuery,
  toSearchParams,
  todayISO,
  type StatsUnit,
} from '../lib/filters';
import { Footer } from '../components/Footer';

/**
 * Statistiky — the Rezervace filter set answered as totals instead of rows.
 *
 * In the legacy extranet this is the second tab of the Rezervace screen and it
 * shares that screen's filterbox; the NextGen navigation only promotes it to
 * its own item. So the toolbar below is the Rezervace toolbar — same controls,
 * same order, same words, same URL parameters — and the filters live in the
 * address bar for the same reasons they do there. A view narrowed on Rezervace
 * can be pasted onto this screen and describes the same set of bookings.
 *
 * The parsing and the query construction come from ../lib/filters, which
 * imports nothing and is covered by `npm run verify:web`. Nothing about the
 * filter semantics is re-implemented here, including the one that matters
 * most: a free-text search drops the date bounds, because somebody typing a
 * name is asking about the database and not about the fortnight on screen.
 */
const CATALOGUE = {
  statuses: ReservationStatus.options as readonly string[],
  sorts: RESERVATION_SORTS as readonly string[],
  // The API's own ceiling, quoted rather than copied.
  maxPage: MAX_PAGE,
  // The five yards plus 'none' — the same list SiteFilter's enum is built
  // from, so the control and the schema cannot offer different options.
  sites: SITE_FILTERS,
  clubs: CLUB_FILTERS,
  sources: SOURCE_FILTERS,
  types: RESERVATION_TYPES,
};

/**
 * Titles for the seven breakdowns, in the order they are shown.
 *
 * Each one is the word the column already has on the screen the staff use
 * every day, declined into the genitive the title needs — not a synonym for
 * it. Rezervace heads that column `Typ` and `Lokalita`; Klienti heads it
 * `Úroveň`. (Rezervace shows the location under `Stání` rather than in a
 * column of its own, but `Lokalita` is its word for it — it is the detail
 * screen's field label.) An earlier draft here said
 * `produktu`, `parkoviště` and `klubu`,
 * which are all perfectly good Czech and all three make a dispatcher stop and
 * work out whether they are looking at the same thing. They are not new
 * concepts and they must not get new names.
 */
const DIM_TITLE: Record<StatsDimension, string> = {
  type: 'Podle typu',
  status: 'Podle stavu',
  location: 'Podle lokality',
  club: 'Podle úrovně klubu',
  source: 'Podle zdroje',
  partner: 'Podle partnera',
  hour: 'Podle hodiny příjezdu',
};

/**
 * A bucket's human form.
 *
 * Every map here is the one the Rezervace grid already reads from, so a slice
 * labelled "Economy" on this screen is the same word an operator sees on the
 * row. Only `partner` carries its label in the data, because a partner's name
 * is not vocabulary.
 */
function bucketLabel(dim: StatsDimension, b: StatsBucket): string {
  if (b.label) return b.label;
  switch (dim) {
    case 'type':
      return TYPE_SHORT[b.key] ?? b.key;
    case 'status':
      return STATUS_LABEL[b.key] ?? b.key;
    case 'location':
      // A NULL parking_location is a real row in this schema, and an empty
      // slice name reads as a rendering fault rather than as missing data.
      return LOCATION_LABEL[b.key] ?? (b.key || '—');
    case 'club':
      return TIER_LABEL[b.key] ?? b.key;
    case 'source':
      return SOURCE_LABEL[b.key] ?? b.key;
    case 'partner':
      return b.key ? `Partner ${b.key}` : 'Bez partnera';
    case 'hour': {
      // Number() so this reads '7:00' like the dashboard's hour axis, rather
      // than the '07:00' the two-character SQL bucket key arrives as.
      //
      // The guard is not defensive programming for its own sake. A row with
      // no arrival at all buckets to the empty string, and Number('') is 0 —
      // so those rows used to print as '0:00' and join the genuine midnight
      // bar, which is a real operational hour at an airport car park and the
      // last one that should be quietly inflated.
      //
      // Note what this does NOT catch, because an earlier version of this
      // comment claimed it did: SUBSTR is 1-indexed, so the legacy
      // '0000-00-00 00:00:00' sentinel yields the characters '00' — a
      // well-formed hour that no amount of checking here can tell from a car
      // that arrived at 00:05. That one has to be handled where the date is
      // still intact, and it is: see STATS_GROUP_SPEC.hour in
      // api/src/read/sql.ts, which maps the sentinel to the empty string so
      // it arrives here as the unknown bucket.
      const h = /^\d{2}$/.test(b.key) ? Number(b.key) : NaN;
      return h >= 0 && h <= 23 ? `${h}:00` : 'neuvedeno';
    }
    default:
      return b.key || '—';
  }
}

/**
 * A date bucket's axis label. The API buckets on a string prefix, so a day is
 * 'YYYY-MM-DD' and a month is 'YYYY-MM' — and a row whose date column holds
 * the legacy '0000-00-00 00:00:00' sentinel arrives as a bucket of zeroes.
 * Those rows are real bookings with an unusable `created`, so they are shown
 * and named rather than dropped; dropping them would make the bars stop
 * adding up to the headline.
 */
function seriesLabel(key: string, unit: 'day' | 'month', full = false): string {
  // Two unusable shapes, not one. The sentinel is '0000-00-00'; a row whose
  // date column is NULL buckets to the empty string, and ''.split('-') hands
  // back undefined for the month and the day, so the axis printed
  // 'NaN. NaN.' — and now that the bars carry accessible names, a screen
  // reader would have said it out loud. Both are real rows with an unusable
  // date, so both are named rather than dropped: dropping them would make the
  // bars stop adding up to the headline.
  if (!key || key.startsWith('0000')) return 'neuvedeno';
  const [y, m, d] = key.split('-');
  if (!y || !m || (unit === 'day' && !d)) return 'neuvedeno';
  // '24. 7.', with the space — that is how every other date in this
  // application is written (splitDt in ../lib/filters, which every grid's
  // date column and the reservation detail both go through), and an axis that
  // spells dates differently from the grid beside it is a small, constant
  // reminder that the two were built by different people.
  //
  // `full` adds the year, and only the tooltip and the accessible name ask for
  // it. Day granularity is not confined to short windows: resolveStatsUnit
  // picks it automatically only under STATS_DAY_MAX_SPAN, but ?unit=day is a
  // toolbar choice that survives any window, including a search, which drops
  // the date filter and reads the whole database. Across several years '1. 1.'
  // then appears once per year with nothing to tell the bars apart. On the
  // axis its neighbours supply the year; a hover tooltip and a screen reader
  // have no neighbours, so those two say it.
  if (unit === 'month') return `${Number(m)}/${y}`;
  return full ? `${Number(d)}. ${Number(m)}. ${y}` : `${Number(d)}. ${Number(m)}.`;
}

/** What the value column and the bars are measuring, said in words. */
const measureWord = (money: boolean) => (money ? 'Tržby v Kč' : 'Počet rezervací');

/* ------------------------------------------------------------------ *
 * Time series
 * ------------------------------------------------------------------ */

/**
 * One bar per day or month, scaled the same way the dashboard's shift chart
 * is: the top of the axis is the data rounded up to a multiple of three, so
 * the three gridlines land on whole numbers and a quiet week is not flattened
 * into a row of stubs.
 *
 * Past twenty-four bars the x-axis is thinned rather than dropped — every
 * label would overlap, but no label at all leaves the shape unreadable. The
 * bars themselves close up later, at forty, because they stay legible for
 * longer than the text under them does.
 */
const XLAB_MAX = 24;
const DENSE_BARS = 40;

function Series({
  buckets,
  unit,
  dateField,
  money,
}: {
  buckets: StatsBucket[];
  unit: 'day' | 'month';
  dateField: DateField;
  money: boolean;
}) {
  const value = (b: StatsBucket) => (money ? b.price : b.count);
  const peak = Math.max(1, ...buckets.map(value));
  const step = Math.max(1, Math.ceil(peak / 3));
  const top = step * 3;
  const px = (v: number) => `${(v / top) * 150}px`;
  const dense = buckets.length > DENSE_BARS;
  const every = Math.max(1, Math.ceil(buckets.length / XLAB_MAX));

  return (
    <div className="card chartcard statseries">
      {/* "Po dnech" / "Po měsících" — the words the granularity select in the
          toolbar uses. An earlier draft said "podle dnů" here, which is a
          second name for a control that already has one, three inches away. */}
      <h4>
        {money ? 'Tržby' : 'Rezervace'} · {unit === 'day' ? 'po dnech' : 'po měsících'} ·{' '}
        {DATEFIELD_LABEL[dateField]}
      </h4>
      {buckets.length === 0 ? (
        // Not "ve zvoleném období": while a search term is set the date filter
        // is switched off and the whole database is being read, so naming a
        // period would be telling the manager to go and widen a filter that is
        // not currently doing anything.
        <div className="state">Pro tento výběr nejsou žádné rezervace.</div>
      ) : (
        <>
          <div className={`bars${dense ? ' dense' : ''}`} role="list">
            {/* Gridlines only when there is something to measure against. An
                all-zero period drew 1 / 2 / 3 over an empty pane, which reads
                as a chart that failed to load rather than as a quiet week.
                The test is on `top`, not on `peak`: peak is floored at 1, so
                `peak > 1` would also have stripped the scale off a real week
                whose busiest day had exactly one booking. */}
            {/* aria-hidden, and not only because a scale read out as three
                bare numbers is noise: .bars carries role="list", and a list
                whose first three children have no role is a list an
                accessibility checker reports and some readers miscount. Every
                bar names its own value, so nothing is lost. */}
            {top > 1 &&
              [1, 2, 3].map((n) => (
                <div key={n} className="gridline" aria-hidden="true" style={{ bottom: `${n * 33}%` }}>
                  <span>{CZK.format(step * n)}</span>
                </div>
              ))}
            {buckets.map((b) => (
              // data-tip is a CSS hover tooltip: it does not exist for anyone
              // reading with a screen reader, and there is nothing else on a
              // bar to read. The same sentence goes in as the accessible name
              // so the chart is a list of figures rather than silence.
              <div
                key={b.key}
                className="col"
                role="listitem"
                aria-label={`${seriesLabel(b.key, unit, true)} — ${CZK.format(b.count)} ${rezWord(b.count)}, ${CZK.format(b.price)} Kč`}
                data-tip={`${seriesLabel(b.key, unit, true)} — ${CZK.format(b.count)} ${rezWord(b.count)}, ${CZK.format(b.price)} Kč`}
              >
                <div className="stack">
                  {/* --chart-dep, not --c1: it is the same blue, but .chartcard
                      is where the mockup lightens it for dark mode, and this
                      chart is inside a .chartcard. */}
                  <div
                    className="seg"
                    style={{ height: px(value(b)), background: 'var(--chart-dep)' }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="baseline" />
          {/* The axis repeats names the bars now carry themselves, so it is
              hidden from assistive technology rather than read out twice. */}
          <div className={`xlab${dense ? ' dense' : ''}`} aria-hidden="true">
            {buckets.map((b, i) => (
              <span key={b.key}>{i % every === 0 ? seriesLabel(b.key, unit) : ''}</span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Breakdown
 * ------------------------------------------------------------------ */

/**
 * One dimension as a list of rows rather than a column chart.
 *
 * The markup is the mockups' own stats pane: .statcard for the card, .cap for
 * the caption under its title, and .hbars .r — name, track, fill, value — for
 * the rows. Nothing about the shape of this card is this screen's invention,
 * which is the point; the only cell added to the mockup's row is the share in
 * percent.
 *
 * A row list is the right shape here for a plain reason: these categories have
 * names of very different lengths — "Bez partnera" next to "Dovolená Parking"
 * — and rotated x-labels are the usual way that becomes unreadable.
 *
 * The caption says what the numbers are, because the same column means
 * bookings or crowns depending on a checkbox eleven controls away in the
 * toolbar, and a column of bare numbers does not say which.
 */
function Breakdown({ group, total, money }: { group: StatsGroup; total: number; money: boolean }) {
  const value = (b: StatsBucket) => (money ? b.price : b.count);
  const max = Math.max(1, ...group.buckets.map(value));
  const sum = group.buckets.reduce((a, b) => a + value(b), 0);
  // Rounding, not equality: the folded partner tail is computed by subtraction
  // and the money columns are decimals in the production schema, so a strict
  // !== would eventually fire this warning on arithmetic that is correct.
  const mismatch = Math.abs(sum - total) >= 0.5;

  /**
   * The rows in the order of the number they are showing.
   *
   * The server ranks these six dimensions by booking count, which is the right
   * order for the screen as it normally stands. But "Zobrazit tržby" swaps
   * every value column to crowns without refetching anything, and a list still
   * in count order then draws bars that go down, up and down again — on the
   * staging archive the whole-database partner card puts 520 bookings worth
   * 139 715 Kč above 181 bookings worth 239 910 Kč, and a thirty-day window
   * puts Airport above Tuchoměřice while Tuchoměřice earns more. A bar chart
   * whose bars are not in order of length is read as a broken chart.
   *
   * `group.order` decides whether re-ranking is allowed at all: the hour
   * histogram is the clock and its bars stay where they are, whatever they are
   * measuring. And the folded tail stays last however it sorts, because the
   * line under the card calls it "posledního řádku" and because it is not a
   * category — it is everything that is not one.
   */
  const ordered = (() => {
    if (!money || group.order !== 'count') return group.buckets;
    const tail = group.truncated ? group.buckets.slice(-1) : [];
    const named = group.truncated ? group.buckets.slice(0, -1) : group.buckets;
    return [...named].sort((a, b) => value(b) - value(a)).concat(tail);
  })();

  return (
    <div className="card statcard">
      <h4>{DIM_TITLE[group.dim]}</h4>
      <div className="cap">{measureWord(money)}</div>
      {group.buckets.length === 0 ? (
        <div className="state">Žádná data.</div>
      ) : (
        <div className="hbars" role="list">
          {ordered.map((b) => {
            const v = value(b);
            const share = sum > 0 ? (v / sum) * 100 : 0;
            const name = bucketLabel(group.dim, b);
            // The row is four cells with no column headings, and the grey
            // track carries no text at all, so read aloud it is a name
            // followed by two bare numbers. The label says which is which.
            const said = `${name}: ${CZK.format(v)} ${money ? 'Kč' : rezWord(v)}, ${PCT.format(share)} %`;
            return (
              <div className="r" key={b.key} role="listitem" aria-label={said}>
                <div className="n" title={name} aria-hidden="true">
                  {name}
                </div>
                <div className="t" aria-hidden="true">
                  <i style={{ width: `${(v / max) * 100}%` }} />
                </div>
                <div className="v" aria-hidden="true">
                  {CZK.format(v)}
                </div>
                <div className="pc" aria-hidden="true">
                  {PCT.format(share)} %
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* The cap is a number, so it is said as one. "zbytek je sečten do
          posledního řádku" left a manager counting rows to work out how many
          partners the last one stands for — and the answer to "how many
          partners do we have" is not on this screen at all otherwise. The
          .basis treatment is the one Klienti uses for exactly this job:
          a quiet line under a figure explaining how the figure was reached. */}
      {/* buckets.length - 1: when a dimension is capped the API appends the
          folded tail as an extra row (see stats.ts), so the number of named
          categories above it is one less than the list length. */}
      {/* "s nejvíce rezervacemi", not "nejsilnějších". The cap is applied in
          SQL, by booking count, before the browser has been told which measure
          is on screen — so under "Zobrazit tržby" the twelve rows are still
          the twelve busiest partners and not the twelve biggest earners. On
          the staging archive that is not hypothetical: a partner worth 31 664
          Kč across seventeen bookings is folded into the tail while one worth
          30 428 Kč is shown. The rows can be re-ranked in the browser and are;
          which twelve of them there are cannot be, so it is said instead of
          left to be discovered. */}
      {group.truncated && (
        <div className="basis">
          Zobrazeno {CZK.format(group.buckets.length - 1)} s nejvíce
          rezervacemi
          {money ? ' (pořadí podle tržeb, výběr podle počtu)' : ''}; vše
          ostatní je sečteno do posledního řádku.
        </div>
      )}
      {/* Every breakdown partitions the same filtered set, so this line is a
          live reconciliation rather than decoration: if a dimension ever stops
          adding up to the headline, it says so on screen. */}
      {mismatch && (
        <div className="brkwarn">
          Součet {CZK.format(sum)} se neshoduje s celkem {CZK.format(total)}.
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

const TABS = ['', 'partneri', 'promokody', 'prodane', 'reklama', 'poptavka', 'hodiny', 'stary', 'automista'] as const;
type Tab = (typeof TABS)[number];
const TAB_LIST: ReadonlyArray<[Tab, string]> = [
  ['', 'Přehled'],
  ['partneri', 'Partneři'],
  ['promokody', 'Promokódy'],
  ['prodane', 'Prodané služby a upgrady'],
  ['reklama', 'Reklama'],
  ['poptavka', 'Poptávka'],
  ['hodiny', 'Hodiny'],
  ['stary', 'Starý systém'],
  ['automista', 'Auto místa'],
];

export function Statistiky() {
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => ({ ...parseFilters(sp, CATALOGUE), ...statsDefaults(sp) }), [sp]);

  /**
   * Granularity and measure both live in the URL, for the same reason the
   * filters do: a link to this screen should land on the view it was shared
   * from. An earlier draft kept `unit` in the URL and `money` in component
   * state, which made two adjacent checkboxes in the same toolbar behave in
   * opposite ways — the kind of inconsistency nobody reports and everybody
   * trips over.
   *
   * Both are read through the same recognise-or-default treatment every other
   * parameter gets in ../lib/filters. A hand-edited ?unit=weekly used to reach
   * the API unchecked and come back as a 400 the screen could not recover from.
   */
  const unit = statsUnit(sp.get('unit'));
  const money = sp.get('money') === '1';
  /**
   * The three legacy reports ride this screen as tabs — Partneři, Promokódy,
   * Prodané služby — so the toolbar above them is the one the staff already
   * know and the menu does not change ("don't change the names on menus").
   * `?tab=` is read the same recognise-or-default way as everything else.
   */
  const tab = oneOf(sp.get('tab'), TABS, '' as Tab);
  const soldPage = pageParam(sp.get('p'), MAX_PAGE);
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });

  /** The URL this screen is showing, with its two extra parameters kept. */
  const searchParams = (f: Filters, extra?: { unit?: StatsUnit; money?: boolean; tab?: Tab; p?: number }) => {
    const next = toSearchParams(f);
    const u = extra?.unit ?? unit;
    const m = extra?.money ?? money;
    const t = extra?.tab ?? tab;
    const pg = extra?.p ?? (extra?.tab !== undefined && extra.tab !== tab ? 1 : soldPage);
    if (u !== 'auto') next.set('unit', u);
    if (m) next.set('money', '1');
    if (t) next.set('tab', t);
    if (t === 'prodane' && pg > 1) next.set('p', String(pg));
    return next;
  };

  const apply = (patch: Partial<Filters>, extra?: { unit?: StatsUnit; money?: boolean; tab?: Tab; p?: number }) => {
    setSp(searchParams({ ...filters, ...patch, page: 1 }, { p: 1, ...extra }), { replace: true });
  };

  /**
   * Push the effective filters back into the address bar.
   *
   * This runs on mount, and the mount case is the point: the thirty-day
   * default above is applied in memory, so without this the screen would show
   * one window and the URL a person copies would carry none. After this fires
   * the URL always states the window it is showing, which is the property the
   * whole filters-in-the-URL design rests on.
   */
  useEffect(() => {
    setSp(searchParams(filters), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- free-text box, debounced against the URL — the Rezervace pattern ---- */
  const [term, setTerm] = useState(filters.q);
  const settled = useRef(filters.q);

  useEffect(() => {
    if (filters.q !== settled.current) {
      settled.current = filters.q;
      setTerm(filters.q);
    }
  }, [filters.q]);

  useEffect(() => {
    if (term === settled.current) return;
    const id = setTimeout(() => {
      settled.current = term;
      apply({ q: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const searching = isSearching(filters);
  /* Read once per render, not per button: six buttons each calling new Date()
     could straddle midnight and highlight two of themselves. */
  const activeQuick = quickRangeKey(filters, new Date(), PAST_QUICK_RANGES);
  /**
   * /api/stats has no pages and no sort, so those four fields are dropped
   * rather than sent and ignored. They are part of the react-query key, and a
   * key that changes on a value the server never reads refetches nine
   * aggregates for nothing.
   */
  const params = useMemo(() => {
    const { sort: _s, dir: _d, page: _p, perPage: _pp, ...rest } = toQuery(filters, { searchEverything: true });
    return { ...rest, unit };
  }, [filters, unit]);
  const reportParams = useMemo(() => {
    const { sort: _s, dir: _d, page: _p, perPage: _pp, ...rest } = toQuery(filters, { searchEverything: true });
    return rest;
  }, [filters]);

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['stats', params],
    queryFn: () => api.stats(params),
    placeholderData: keepPreviousData,
    // A report tab shows its own table; the nine aggregates are not fetched
    // behind it.
    enabled: tab === '',
  });

  /** Back to the screen a bare /statistiky shows — including its two extras. */
  const reset = () => {
    setTerm('');
    settled.current = '';
    const d = makeDefaults();
    setSp(
      searchParams(
        { ...d, dateField: 'arrival', from: todayISO(-STATS_LOOKBACK_DAYS), to: todayISO(0) },
        { unit: 'auto', money: false, tab: '', p: 1 },
      ),
      { replace: true },
    );
  };

  /* The same 403 treatment Klienti uses: a plain sentence about whose screen
     this is, and where the number an operator actually needs already lives.
     The navbar shows this item to every role, so for a dispatcher and a
     driver THIS is the Statistiky screen — it is the only part of it most of
     the staff will ever read. Which means the directions in it have to be
     literally correct: an earlier draft sent them to the footer of Rezervace
     (there are no totals there — the footer is the phase-0 notice) to look
     for "nedoplatky" (that word is not on the screen; the toolbar says
     "zbývá"). Three sentences of wrong directions handed to the exact people
     who cannot check them. Every place named below was read off Rezervace.tsx
     rather than remembered. */
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="container">
        <div className="pagehead">
          <h1>Statistiky</h1>
        </div>
        <div className="card state">
          Statistiky jsou přístupné jen manažerům a administrátorům. Celkovou
          cenu a zbývající částku za právě nastavený filtr najdete
          v Rezervacích na konci řádku s filtry; počet rezervací je vedle
          nadpisu.
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const paidShare = s && s.price > 0 ? Math.round((s.paid / s.price) * 100) : 0;

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Statistiky</h1>
        <span className="sub recordcount">
          {tab !== '' ? 'sestavy' : data ? `${CZK.format(data.summary.count)} ${rezWord(data.summary.count)}` : '…'}
          {tab === '' && isFetching && !isPending ? ' · aktualizuji' : ''}
        </span>
        <div className="acts">
          <form onSubmit={(e) => e.preventDefault()} role="search">
            <label className="visually-hidden" htmlFor="q">
              Hledat rezervace
            </label>
            <input
              id="q"
              type="search"
              placeholder="ID, jméno, e-mail, telefon, SPZ…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </form>
        </div>
      </div>

      <div className="card toolbar">
        <select
          value={filters.dateField}
          disabled={searching}
          onChange={(e) => apply({ dateField: e.target.value as DateField })}
          aria-label="Které datum filtrovat"
        >
          <option value="arrival">Podle příjezdu</option>
          <option value="departure">Podle odjezdu</option>
          <option value="created">Podle vytvoření</option>
        </select>
        <input
          type="date"
          value={filters.from}
          disabled={searching}
          onChange={(e) => apply({ from: e.target.value })}
          aria-label="Datum od"
        />
        <span style={{ color: 'var(--ink-3)' }}>–</span>
        <input
          type="date"
          value={filters.to}
          disabled={searching}
          onChange={(e) => apply({ to: e.target.value })}
          aria-label="Datum do"
        />

        {/* The same strip as Rezervace, in the same place, with the two
            forward-looking entries swapped for the ones a report is actually
            asked for. See PAST_QUICK_RANGES. */}
        <div className="quickrange" role="group" aria-label="Rychlý výběr období">
          {PAST_QUICK_RANGES.map((qr) => (
            <button
              key={qr.key}
              type="button"
              className={activeQuick === qr.key ? 'qbtn on' : 'qbtn'}
              disabled={searching}
              title={qr.title}
              aria-pressed={activeQuick === qr.key}
              onClick={() => apply(qr.range(new Date()))}
            >
              {qr.label}
            </button>
          ))}
        </div>

        <span className="sep" />

        <select
          value={filters.site}
          onChange={(e) => apply({ site: e.target.value })}
          aria-label="Kde auto stojí"
          title="Parkoviště, na kterém auto stojí"
        >
          <option value="">Kde stojí: vše</option>
          {SITE_FILTERS.map((sv) => (
            <option key={sv} value={sv}>
              {SITE_FILTER_LABEL[sv]}
            </option>
          ))}
        </select>

        <select
          value={filters.status[0] ?? ''}
          onChange={(e) => apply({ status: e.target.value ? [e.target.value] : [] })}
          aria-label="Stav rezervace"
        >
          <option value="">Všechny stavy</option>
          {ReservationStatus.options
            .filter((st) => st !== 'deleted')
            .map((st) => (
              <option key={st} value={st}>
                {STATUS_LABEL[st]}
              </option>
            ))}
        </select>

        <label className="chk">
          <input
            type="checkbox"
            checked={filters.unpaidOnly}
            onChange={(e) => apply({ unpaidOnly: e.target.checked })}
          />
          Jen nedoplatky
        </label>
        <label className="chk">
          <input
            type="checkbox"
            checked={filters.missingCarOnly}
            onChange={(e) => apply({ missingCarOnly: e.target.checked })}
          />
          Chybí auto / SPZ
        </label>

        <span className="sep" />

        <select
          value={unit}
          onChange={(e) => apply({}, { unit: statsUnit(e.target.value) })}
          aria-label="Graf po dnech nebo po měsících"
        >
          <option value="auto">Automaticky</option>
          <option value="day">Po dnech</option>
          <option value="month">Po měsících</option>
        </select>

        <label className="chk">
          <input
            type="checkbox"
            checked={money}
            onChange={(e) => apply({}, { money: e.target.checked })}
          />
          {/* Not "Měřit tržbami". This checkbox does not add anything — it
              swaps what every bar, every value column and every share on the
              screen is counting, and the label has to say that it is a
              replacement or a manager reads the crowns as bookings. */}
          Zobrazit tržby místo počtu
        </label>

        <button className="btn sm" type="button" onClick={reset}>
          Zrušit filtry
        </button>
      </div>

      <div className="tabs sestavy" role="tablist" aria-label="Statistiky a sestavy">
        {TAB_LIST.map(([key, label]) => (
          <button
            key={key || 'prehled'}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={tab === key ? 'active' : undefined}
            onClick={() => apply({}, { tab: key, p: 1 })}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'partneri' && <GroupReport kind="partners" params={reportParams} />}
      {tab === 'promokody' && <GroupReport kind="promocodes" params={reportParams} />}
      {tab === 'reklama' && <AdsReport from={filters.from} to={filters.to} />}
      {tab === 'poptavka' && <DemandReportView from={filters.from} to={filters.to} />}
      {tab === 'hodiny' && <HoursReportView from={filters.from} to={filters.to} />}
      {tab === 'stary' && <UsageReportView from={filters.from} to={filters.to} />}
      {tab === 'automista' && <AutoTargetReportView from={filters.from} to={filters.to} />}
      {tab === 'prodane' && (
        <SoldReport
          from={filters.from}
          to={filters.to}
          q={filters.q}
          page={soldPage}
          onPage={(p) => apply({}, { p })}
          canTick={can(me.data, '/reports/*', 'write')}
        />
      )}
      {tab === 'prodane' && (
        <div className="notice" role="note">
          Prodané služby se filtrují jen podle <b>data prodeje</b> (od–do nahoře) a hledání; stav, typ a parkoviště se na tuhle
          sestavu nevztahují.
        </div>
      )}

      {tab === '' && searching && (
        <div className="notice" role="status">
          Hledá se v <b>celé databázi</b> — datumový filtr je po dobu hledání
          vypnutý. Vymažte hledaný výraz a filtr se vrátí.
        </div>
      )}

      {/* Skeletons, not the word "Načítám…". Every other screen in this
          application draws the shape of what is coming — see Rezervace and
          Klienti — and a stats screen that instead blanks to a single line of
          text looks, for the second or two the query takes, like a screen that
          has lost its data rather than one that is fetching it.

          The wrapper carries role="status" rather than being a bare div: ARIA
          prohibits aria-label on a generic element, so the name was being
          dropped and the loading state announced nothing at all — the opposite
          of what the label was added for. */}
      {tab === '' && isPending && (
        <div role="status" aria-busy="true" aria-label="Načítám statistiky">
          <div className="kpis" style={{ marginTop: 14 }}>
            {[0, 1, 2, 3].map((i) => (
              <div className="card kpi" key={i}>
                <div className="skeleton" style={{ width: '45%' }} />
                <div className="skeleton" style={{ width: '70%', marginTop: 10, height: 20 }} />
                <div className="skeleton" style={{ width: '60%', marginTop: 10 }} />
              </div>
            ))}
          </div>
          <div className="card chartcard">
            <div className="skeleton" style={{ width: '38%' }} />
            <div className="skeleton" style={{ height: 150, marginTop: 14, borderRadius: 8 }} />
          </div>
        </div>
      )}
      {/* role="alert" so the failure is announced rather than only drawn, the
          server's own words so a support call has something to quote, and a
          button, because the alternative a manager is left with is reloading
          the page and losing the filters they just set. */}
      {tab === '' && isError && !(error instanceof ApiError && error.status === 403) && (
        <div className="card state" role="alert">
          Statistiky se nepodařilo načíst.
          {error instanceof Error && error.message ? ` (${error.message})` : ''}{' '}
          <button className="btn sm" type="button" onClick={() => refetch()}>
            Zkusit znovu
          </button>
        </div>
      )}

      {tab === '' && data && s && (
        <>
          <div className="kpis" style={{ marginTop: 14 }}>
            <div className="card kpi">
              <div className="l">Rezervací</div>
              <div className="v">{CZK.format(s.count)}</div>
              <div className="d">bez smazaných</div>
            </div>
            <div className="card kpi">
              <div className="l">Tržby</div>
              <div className="v">{CZK.format(s.price)} Kč</div>
              <div className="d">průměr {CZK.format(s.avgPrice)} Kč / rezervace</div>
            </div>
            <div className="card kpi">
              <div className="l">Zaplaceno</div>
              <div className="v">{CZK.format(s.paid)} Kč</div>
              <div className="d">{paidShare} % z tržeb</div>
            </div>
            {/* "Zbývá", because that is the word the Rezervace footer and the
                403 card above both already use for this exact figure, and the
                staff are not being retrained. A negative value is not a
                nedoplatek at all — it is money taken above the price — so it
                is named rather than printed as a minus sign the reader has to
                interpret. */}
            <div className="card kpi">
              <div className="l">Zbývá</div>
              <div
                className="v"
                style={{ color: s.outstanding > 0 ? 'var(--critical)' : 'inherit' }}
              >
                {CZK.format(Math.abs(s.outstanding))} Kč
              </div>
              <div className="d">
                {s.outstanding < 0 ? 'přeplatek — zaplaceno nad cenu' : 'tržby minus zaplaceno'}
              </div>
            </div>
          </div>

          <Series
            buckets={data.series}
            unit={data.unit}
            dateField={data.dateField}
            money={money}
          />

          <div className="statgrid">
            {data.groups.map((g) => (
              <Breakdown
                key={g.dim}
                group={g}
                total={money ? s.price : s.count}
                money={money}
              />
            ))}
          </div>

          {/* One thing about this data a manager should not have to discover by
              being surprised by a chart. It is a fact about the database, not
              about this screen. The single-value "source" breakdown is not
              mentioned: that is true of the anonymised staging copy and may
              well not be true of production, and hard-coding it here would put
              a staging fact in front of a manager reading live numbers. */}
          <div className="notice" role="note">
            Členění <b>podle hodiny</b> se vždy počítá z příjezdu, i když je
            filtr nastavený na odjezd nebo vytvoření.
          </div>
        </>
      )}

      {/* The same footer every other screen carries. It was missing here, and
          this is the one screen whose whole output is numbers a manager might
          quote to somebody — so it is the last screen that should leave the
          reader guessing whether they are looking at staging or at the
          business. */}
      <Footer />
    </div>
  );
}
